import { useEffect, useRef } from 'react'
import { useApolloClient, useQuery } from '@apollo/client/react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { getActiveTimeZone, setActiveTimeZone, useDeviceTimeZone } from 'lib/hooks/useActiveTimeZone/useActiveTimeZone'
import {
  getTimeZonePreferenceToken,
  isCurrentTimeZonePreference,
  setTimeZonePreferenceSession,
} from 'lib/helpers/timeZonePreferenceSession/timeZonePreferenceSession'
import { refreshTimeZoneText } from 'lib/helpers/refreshTimeZoneText/refreshTimeZoneText'
import { logError } from 'lib/tracking/datadog'
import { InitializeTimeZonePreferenceHook_profileDocument } from './useInitializeTimeZonePreference.graphql'

interface PublishedPreference {
  profileId: string
  enabled: boolean
  timeZone: string
}

export const useInitializeTimeZonePreference = (profileId: string): void => {
  const { timezoneAwareTimestamps = false } = useFlags()
  const client = useApolloClient()
  const { data } = useQuery(InitializeTimeZonePreferenceHook_profileDocument)
  const deviceTimeZone = useDeviceTimeZone()
  const publishedPreference = useRef<Maybe<PublishedPreference>>(null)
  const lastAccountRefresh = useRef<Maybe<string>>(null)

  useEffect(() => {
    const previousTimeZone = getActiveTimeZone()

    if (setTimeZonePreferenceSession(profileId)) {
      setActiveTimeZone(null)
    }

    const profile = data?.profile

    if (profile && profile.id !== profileId) {
      if (lastAccountRefresh.current !== profileId) {
        lastAccountRefresh.current = profileId
        const token = getTimeZonePreferenceToken(profileId)

        void client
          .query({ query: InitializeTimeZonePreferenceHook_profileDocument, fetchPolicy: 'no-cache' })
          .then((current) => {
            if (isCurrentTimeZonePreference(token) && current.data?.profile?.id === profileId) {
              client.writeQuery({ query: InitializeTimeZonePreferenceHook_profileDocument, data: current.data })
            }
          })
          .catch(logError)
      }

      return
    }

    if (!profile) {
      return
    }

    setActiveTimeZone(timezoneAwareTimestamps ? profile.timezone : null, { hasProfilePreference: true })
    const next = { profileId, enabled: timezoneAwareTimestamps, timeZone: getActiveTimeZone() }
    const previous = publishedPreference.current

    publishedPreference.current = next

    if (
      previous
        ? previous.profileId !== next.profileId ||
          previous.enabled !== next.enabled ||
          previous.timeZone !== next.timeZone
        : previousTimeZone !== next.timeZone
    ) {
      void refreshTimeZoneText(client).catch(logError)
    }
  }, [client, data, deviceTimeZone, profileId, timezoneAwareTimestamps])
}
